// frontend/src/components/OperationsPlanDashboard.js
//
// Operations department dashboard: the Director of Operations variable comp plan
// (effective Q4 2026), calculated live for the selected quarter.
//
//   Component 1  Services Contribution — invoiced services income (NetSuite income
//                statement, Services departments) against the Services Income Floor,
//                straight-line bands, no ceiling, zero if contribution is negative.
//   Component 2  Rocks — payout % = Rocks completed / Rocks set. Rocks come from
//                L10 (the payee's own, src/l10.js); hand entry is the fallback.
//   Gate         Optional Company GP Floor multiplier (only when Finance sets one).
//
// All math runs on the server (/api/ops-plan); this page only renders it. Floors,
// the target and the Rocks are editable here by global admins only.
import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { getOpsPlan, refreshOpsPlan, updateOpsPlanSettings, addOpsRock, updateOpsRock, deleteOpsRock, resetOpsRockStatus } from '../api/client';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const pct = (x, d = 1) => x == null ? '—' : `${(x * 100).toFixed(d)}%`;
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Quarters the plan can show: from its first quarter through the current quarter + 1.
function quarterOptions() {
  const now = new Date();
  const curY = now.getUTCFullYear(), curQ = Math.ceil((now.getUTCMonth() + 1) / 3);
  const out = [];
  let y = 2026, q = 4;
  while (y < curY || (y === curY && q <= curQ + 1) || (y === curY + 1 && curQ === 4 && q === 1)) {
    out.push(`${y}-Q${q}`);
    if (q === 4) { y++; q = 1; } else q++;
    if (out.length > 12) break;
  }
  return out;
}
const currentQuarter = () => { const n = new Date(); return `${n.getUTCFullYear()}-Q${Math.ceil((n.getUTCMonth() + 1) / 3)}`; };

export default function OperationsPlanDashboard({ members = [], label = 'Operations', isMine = false }) {
  const { user } = useAuth();
  const options = useMemo(quarterOptions, []);
  const [quarter, setQuarter] = useState(() => options.includes(currentQuarter()) ? currentQuarter() : options[options.length - 1]);
  const { data, loading, error, refetch } = useApi(() => getOpsPlan(quarter), [quarter]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const doRefresh = async () => {
    setRefreshing(true); setRefreshError(null);
    try {
      const r = await refreshOpsPlan(quarter);
      if (r?.refreshErrors?.length) setRefreshError(r.refreshErrors.join(' · '));
      await refetch();
    }
    catch (e) { setRefreshError(e.message); }
    finally { setRefreshing(false); }
  };

  const canEdit = !!data?.canEdit;

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Department{isMine ? ' · yours' : ''} · Director of Operations plan</div>
          <h1 style={{ fontSize: '1.6rem' }}>{label}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={quarter} onChange={e => setQuarter(e.target.value)} className="input" style={{ width: 'auto', minWidth: '130px' }}>
            {options.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          <button onClick={doRefresh} disabled={refreshing || loading} className="btn btn-ghost btn-sm" title="Pull the services income statement from NetSuite and the Rocks from L10 now">
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>
      ) : error && !data ? (
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '6px' }}>Plan unavailable</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{typeof error === 'string' ? error : 'Could not load the Operations plan.'}</p>
        </div>
      ) : data ? (
        <PlanBody data={data} canEdit={canEdit} refetch={refetch} refreshError={refreshError} user={user} />
      ) : null}

      <MembersCard label={label} members={members} />
    </div>
  );
}

function PlanBody({ data, canEdit, refetch, refreshError }) {
  const { calc, settings, servicesPl, rocks, rocksSync, quarter, period, companyGp } = data;
  const { comp1, comp2, gate, subtotal, total, quarterlyTarget } = calc;

  return (
    <>
      {refreshError && <Notice tone="red">Refresh failed — {refreshError}. Showing the last stored figures.</Notice>}
      {!servicesPl && <Notice tone="amber">No services income has been pulled for {quarter} yet. Click Refresh.</Notice>}

      {/* ── Headline tiles ── */}
      <div className="kpi-grid-4" style={{ gap: '12px', marginBottom: '20px' }}>
        <Tile label="Services income (invoiced)" value={fmt(comp1.servicesIncome)} sub={`${period.start} → ${period.end} · posting period`} />
        <Tile label="Services Income Floor" value={fmt0(comp1.floor)} sub={settings.isDefault ? 'plan default' : 'set by Finance'} />
        <Tile label="Attainment" value={pct(comp1.attainment)} sub={`${pct(comp1.pct, 0)} of Component 1`} accent={comp1.attainment >= 1 ? 'green' : comp1.attainment >= 0.9 ? 'amber' : 'muted'} />
        <Tile label="Projected quarterly payout" value={fmt(total)} sub={`${pct(calc.pctOfTarget, 0)} of ${fmt0(quarterlyTarget)} target`} accent="green" big />
      </div>

      {/* ── Component 1 ── */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
        <CardTitle n="1" title="Services Contribution" tag={`${fmt0(comp1.target)} at target · no ceiling`} color="var(--blue-primary)" />
        <div className="ops-two-col" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '28px', alignItems: 'start' }}>
          <div>
            <Step n="1" desc="Services income invoiced this quarter" detail="NetSuite income statement · Services departments · posting period" value={fmt(comp1.servicesIncome)} />
            <Step n="2" desc={`Attainment: income ÷ ${fmt0(comp1.floor)} Services Income Floor`} value={pct(comp1.attainment, 2)} />
            <Step n="3" desc="Payout % of component at this attainment" detail="Straight line between the band points; same slope continues above 120%" value={pct(comp1.pct, 1)} />
            <Step total desc="Component 1 payout" detail={`${pct(comp1.pct, 1)} × ${fmt0(comp1.target)}`} value={fmt(comp1.payout)} tone={comp1.payout > 0 ? 'green' : undefined} />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '10px', lineHeight: 1.5 }}>
              Data as of <strong>{fmtWhen(servicesPl?.fetchedAt)}</strong>{servicesPl?.stale ? ' · refreshing in the background' : ''}. Refreshed automatically with each sync.
            </div>
          </div>
          <WhereYouStand comp1={comp1} bands={data.plan.comp1Bands} />
        </div>
        <div style={{ marginTop: '22px' }}>
          <BandLadder bands={data.plan.comp1Bands} target={comp1.target} attainment={comp1.attainment} floor={comp1.floor} />
        </div>
      </div>

      {/* ── Component 2: Rocks ── */}
      <RocksCard quarter={quarter} rocks={rocks} comp2={comp2} canEdit={canEdit} refetch={refetch} sync={rocksSync} />

      {/* ── Gate + total ── */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: '20px', borderColor: 'var(--border-bright)' }}>
        <CardTitle title="Quarter total" tag={gate.applied ? 'subtotal × company GP gate' : 'no company GP gate this quarter'} color="var(--green)" />
        <Step desc="Component 1 — Services Contribution" value={fmt(comp1.payout)} />
        <Step desc="Component 2 — Rocks" detail={comp2.defaultedToTarget ? 'No Rocks set — scores at target' : `${comp2.done} of ${comp2.total} complete`} value={fmt(comp2.payout)} />
        <Step desc="Subtotal" value={fmt(subtotal)} />
        {gate.applied ? (
          <Step desc={`Company GP gate: ${fmt0(gate.companyGp)} closed-won GP ÷ ${fmt0(gate.floor)} Floor = ${pct(gate.attainment)}`}
                detail={gate.attainment >= 1 ? 'At or above the Floor — full payout' : `Ramp: 0.60 at 60% of Floor → 1.00 at the Floor${gate.consecutive ? ' · second consecutive miss × 0.75' : ''}`}
                value={`× ${gate.multiplier.toFixed(4)}`} tone={gate.multiplier < 1 ? 'amber' : 'green'} />
        ) : (
          <Step desc="Company GP gate" detail="Not configured for this quarter. The Services Income Floor is the only floor applied. Finance can add a Company GP Floor in the plan settings below." value="× 1.0000" />
        )}
        <Step total desc={`${quarter} projected payout`} detail={`Paid in the month after quarter end · ${pct(calc.pctOfTarget, 0)} of the ${fmt0(quarterlyTarget)} quarterly target · uncapped`} value={fmt(total)} tone="green" />
        <Notice tone="muted" tight>
          <strong>Controls (plan §12).</strong> This is a live projection. Finance recalculates both components independently from the NetSuite income statement, and the CEO approves the payout before it goes to payroll.
        </Notice>
      </div>

      {/* ── Plan settings ── */}
      <SettingsCard quarter={quarter} settings={settings} companyGp={companyGp} canEdit={canEdit} refetch={refetch} />
    </>
  );
}

// ─── Rocks ──────────────────────────────────────────────────────────────────
const STATUS_LABEL = { open: 'In progress', done: 'Complete', missed: 'Missed' };
const STATUS_BADGE = { open: 'badge-pending', done: 'badge-paid', missed: 'badge-disputed' };

function RocksCard({ quarter, rocks, comp2, canEdit, refetch, sync }) {
  const hasL10 = rocks.some(r => r.source === 'l10');
  const l10On = !!sync?.configured;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', outcome: '', owner: '', dueDate: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); await refetch(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const submit = () => run(async () => {
    await addOpsRock(quarter, draft);
    setDraft({ title: '', outcome: '', owner: '', dueDate: '' }); setAdding(false);
  });

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
      <CardTitle n="2" title="Special Projects — Rocks" tag={`${fmt0(comp2.target)} at target · % complete${hasL10 ? ' · from L10' : ''}`} color="var(--blue-sky)"
        right={canEdit && !adding && !hasL10 && <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>+ Add a Rock</button>} />

      <div style={{ display: 'flex', gap: '24px', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div><span style={{ fontSize: '26px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--blue-sky)' }}>{comp2.done} of {comp2.total}</span> <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Rocks complete</span></div>
        <div><span style={{ fontSize: '26px', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{pct(comp2.ratio, 0)}</span> <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>of component</span></div>
        <div><span style={{ fontSize: '26px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--green)' }}>{fmt(comp2.payout)}</span> <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Component 2 payout</span></div>
      </div>
      {comp2.defaultedToTarget && <Notice tone="amber" tight>No Rocks have been set for {quarter}{l10On ? ` — none found in L10 for ${sync.owner}` : ''}. Per the plan the component scores at target until Rocks are entered{l10On ? ' in L10' : ''} — in the first two weeks of the quarter.</Notice>}
      {l10On && sync.ok === false && <Notice tone="red" tight>L10 pull failed: {sync.error}. Showing the last stored Rocks.</Notice>}
      {err && <Notice tone="red" tight>{err}</Notice>}

      {rocks.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: '12px' }}>
          <table className="data-table">
            <thead>
              <tr><th style={{ width: '32px' }}>#</th><th>Rock</th>{hasL10 ? <th>Progress</th> : <th>Owner</th>}<th>Due</th><th>Status</th>{canEdit && <th />}</tr>
            </thead>
            <tbody>
              {rocks.map((r, i) => (
                <tr key={r.id} style={{ opacity: r.status === 'missed' ? 0.7 : 1 }}>
                  <td style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.title}</div>
                    {r.outcome && <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{r.outcome}</div>}
                    {r.source === 'l10' && r.l10_type && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '3px' }}>{r.l10_type} Rock · {r.owner}</div>}
                  </td>
                  {hasL10 ? <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}><RockProgress r={r} /></td> : <td style={{ fontSize: '13px' }}>{r.owner || '—'}</td>}
                  <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{fmtDate(r.due_date)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {canEdit ? (
                      <select value={r.status} disabled={busy} onChange={e => run(() => updateOpsRock(r.id, { status: e.target.value }))} className="input" style={{ width: 'auto', padding: '4px 8px', fontSize: '13px' }}>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    ) : (
                      <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                    )}
                    {r.source === 'l10' && (
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '4px', whiteSpace: 'nowrap' }}>
                        L10: {r.l10_status || '—'}
                        {r.status_override && <> · <span style={{ color: 'var(--amber, #EEBB3F)' }}>overridden</span>{canEdit && <> · <button className="btn-link" disabled={busy} onClick={() => run(() => resetOpsRockStatus(r.id))} style={{ font: 'inherit', color: 'var(--blue-primary)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>use L10</button></>}</>}
                      </div>
                    )}
                  </td>
                  {canEdit && (
                    <td style={{ textAlign: 'right' }}>
                      {r.source !== 'l10' && <button className="btn btn-ghost btn-sm" disabled={busy} title="Remove this Rock" onClick={() => { if (window.confirm(`Remove "${r.title}"?`)) run(() => deleteOpsRock(r.id)); }}>✕</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div style={{ marginTop: '14px', padding: '14px 16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', display: 'grid', gap: '10px', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)' }}>
          <input className="input" placeholder="Rock (what will be true at quarter end)" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} autoFocus />
          <input className="input" placeholder="Owner" value={draft.owner} onChange={e => setDraft(d => ({ ...d, owner: e.target.value }))} />
          <input className="input" type="date" value={draft.dueDate} onChange={e => setDraft(d => ({ ...d, dueDate: e.target.value }))} />
          <input className="input" style={{ gridColumn: '1 / -1' }} placeholder="Written outcome (optional)" value={draft.outcome} onChange={e => setDraft(d => ({ ...d, outcome: e.target.value }))} />
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !draft.title.trim()} onClick={submit}>{busy ? '…' : 'Save Rock'}</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px', lineHeight: 1.5 }}>
        {hasL10 ? (
          <>Pulled from L10 ({(sync?.teams || []).join(', ') || 'WT Leadership Team'}) for <strong>{sync?.owner}</strong> with each sync · last pulled <strong>{fmtWhen(sync?.quarterSyncedAt)}</strong>.
          A Rock counts as complete when L10 marks it complete or its progress reaches 100%; edit the Rock itself in L10. </>
        ) : null}
        Two to five Rocks a quarter, each with a written outcome, a due date and a named owner, entered in the first two weeks. A Rock that slips scores zero in the quarter it was due.
      </div>
    </div>
  );
}

function RockProgress({ r }) {
  const p = Math.max(0, Math.min(100, Number(r.l10_pct) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ width: '72px', height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: p >= 100 ? 'var(--green)' : 'var(--blue-sky)' }} />
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px' }}>{p}%</span>
      {r.milestones_total > 0 && <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{r.milestones_done}/{r.milestones_total} milestones</span>}
    </div>
  );
}

// ─── Settings (global admin) ────────────────────────────────────────────────
function SettingsCard({ quarter, settings, companyGp, canEdit, refetch }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setForm(null); setErr(null); }, [quarter, settings.updatedAt]);

  const start = () => setForm({
    servicesIncomeFloor: settings.servicesIncomeFloor,
    quarterlyTarget:     settings.quarterlyTarget,
    companyGpFloor:      settings.companyGpFloor ?? '',
    priorQuarterMissed:  settings.priorQuarterMissed,
    notes:               settings.notes || '',
  });
  const save = async () => {
    setBusy(true); setErr(null);
    try { await updateOpsPlanSettings(quarter, form); setForm(null); await refetch(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
      <CardTitle title={`Plan settings · ${quarter}`} tag={canEdit ? 'global admins only' : 'read-only'} color="var(--text-secondary)"
        right={canEdit && !form && <button className="btn btn-ghost btn-sm" onClick={start}>Edit</button>} />
      {!form ? (
        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
          <Kv label="Services Income Floor" value={fmt0(settings.servicesIncomeFloor)} sub={settings.isDefault ? 'plan default — not yet published for this quarter' : `set by ${settings.updatedBy || 'Finance'} · ${fmtWhen(settings.updatedAt)}`} />
          <Kv label="Quarterly target" value={fmt0(settings.quarterlyTarget)} sub="50% Services Contribution · 50% Rocks" />
          <Kv label="Company GP Floor (gate)" value={settings.companyGpFloor ? fmt0(settings.companyGpFloor) : 'None'} sub={settings.companyGpFloor ? `closed-won GP this quarter: ${fmt0(companyGp)}` : 'blank = no company gate applied'} />
          <Kv label="Prior quarter missed its Floor?" value={settings.priorQuarterMissed ? 'Yes (× 0.75 on a second miss)' : 'No'} sub="only matters when a Company GP Floor is set" />
          {settings.notes && <Kv label="Notes" value={settings.notes} />}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Services Income Floor ($)"><input className="input" type="number" min="1" step="1000" value={form.servicesIncomeFloor} onChange={e => setForm(f => ({ ...f, servicesIncomeFloor: e.target.value }))} /></Field>
          <Field label="Quarterly target ($)"><input className="input" type="number" min="1" step="500" value={form.quarterlyTarget} onChange={e => setForm(f => ({ ...f, quarterlyTarget: e.target.value }))} /></Field>
          <Field label="Company GP Floor ($) — blank for no gate"><input className="input" type="number" min="0" step="10000" value={form.companyGpFloor} onChange={e => setForm(f => ({ ...f, companyGpFloor: e.target.value }))} placeholder="none" /></Field>
          <Field label="Prior quarter missed its Floor?">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '8px 0' }}>
              <input type="checkbox" checked={!!form.priorQuarterMissed} onChange={e => setForm(f => ({ ...f, priorQuarterMissed: e.target.checked }))} /> Yes — apply the × 0.75 consecutive-miss factor
            </label>
          </Field>
          <Field label="Notes" wide><input className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Floors published 9/25/2026" /></Field>
          {err && <div style={{ gridColumn: '1 / -1' }}><Notice tone="red" tight>{err}</Notice></div>}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? '…' : 'Save settings'}</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px', lineHeight: 1.5 }}>
        Both Floors are published before the quarter opens and are fixed once it has begun (plan §10). Changes here are audit-logged.
      </div>
    </div>
  );
}

// ─── Members ────────────────────────────────────────────────────────────────
const ROLE_LABEL = { rep: 'Rep', manager: 'Manager', finance_admin: 'Finance Admin', global_admin: 'Global Admin' };
function MembersCard({ label, members }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>{label} team ({members.length})</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Department membership is set on Finance → Users.</p>
      </div>
      {members.length === 0 ? (
        <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px' }}>No one is assigned to {label} yet.</div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} style={{ opacity: m.active ? 1 : 0.55 }}>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.display_name || '—'}</td>
                <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{m.email || '—'}</td>
                <td>{ROLE_LABEL[m.app_role] || m.app_role}</td>
                <td><span className={`badge ${m.active ? 'badge-paid' : 'badge-disputed'}`}>{m.active ? 'Active' : 'Inactive'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Small pieces ───────────────────────────────────────────────────────────
function Tile({ label, value, sub, accent = 'blue', big = false }) {
  const color = accent === 'green' ? 'var(--green)' : accent === 'amber' ? 'var(--amber)' : accent === 'muted' ? 'var(--text-muted)' : 'var(--blue-primary)';
  return (
    <div className="card" style={{ padding: '16px 18px', borderColor: big ? 'var(--border-bright)' : undefined }}>
      <div className="label" style={{ marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: big ? '26px' : '22px', fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

function CardTitle({ n, title, tag, color, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
      {n && <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: color, color: '#fff', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>}
      <h3 style={{ margin: 0 }}>{title}</h3>
      {tag && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{tag}</span>}
      {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
    </div>
  );
}

function Step({ n, desc, detail, value, total = false, tone }) {
  const color = tone === 'green' ? 'var(--green)' : tone === 'red' ? 'var(--red)' : tone === 'amber' ? 'var(--amber)' : 'var(--text-primary)';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: total ? '12px 0 4px' : '8px 0', borderTop: total ? '1px solid var(--border)' : undefined, marginTop: total ? '6px' : 0 }}>
      {n && <span style={{ width: '20px', height: '20px', borderRadius: '4px', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: '11px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>{n}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: total ? '14px' : '13.5px', fontWeight: total ? 600 : 400, color: total ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{desc}</div>
        {detail && <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>{detail}</div>}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: total ? 700 : 500, fontSize: total ? '16px' : '14px', color, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

// Band points with one illustrative point above the last (140% → 200%), plus a
// "below 75%" cell, so the current position always lands in exactly one box.
function ladderPoints(bands) {
  const last = bands[bands.length - 1], prev = bands[bands.length - 2];
  const slope = (last.pct - prev.pct) / (last.att - prev.att);
  return { slope, points: [...bands, { att: 1.4, pct: last.pct + (1.4 - last.att) * slope, beyond: true }] };
}
function activeIndex(points, attainment) {
  let idx = -1;                                   // -1 = below the first point
  points.forEach((p, i) => { if (attainment >= p.att) idx = i; });
  return idx;
}

// Progress toward the Floor and what the next band needs.
function WhereYouStand({ comp1, bands }) {
  const { points, slope } = ladderPoints(bands);
  const att = comp1.attainment || 0;
  const idx = activeIndex(points, att);
  const next = points.find(p => p.att > att && !p.beyond) || null;
  const max = Math.max(1.4, att + 0.05);
  const fillPct = Math.min(100, att / max * 100);
  const label = idx < 0 ? 'Below 75% of Floor' : points[idx].beyond ? 'Above 140% of Floor' : `${pct(points[idx].att, 0)}${points[idx].att === 1 ? ' — the Floor' : ''} band`;
  return (
    <div style={{ padding: '16px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
      <div className="label" style={{ marginBottom: '6px' }}>Where you stand</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '30px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: att >= 1 ? 'var(--green)' : 'var(--blue-sky)' }}>{pct(att, 1)}</span>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>of the {fmt0(comp1.floor)} Floor · {label}</span>
      </div>
      {/* Track */}
      <div style={{ position: 'relative', height: '14px', borderRadius: '7px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', margin: '14px 0 22px', overflow: 'visible' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillPct}%`, borderRadius: '7px', background: 'linear-gradient(90deg, #0079C2, #00A4E4)', boxShadow: '0 0 12px rgba(0,164,228,0.45)' }} />
        {points.map(p => {
          const x = Math.min(100, p.att / max * 100);
          const isFloor = p.att === 1;
          return (
            <div key={p.att} style={{ position: 'absolute', left: `${x}%`, top: isFloor ? '-5px' : '-2px', bottom: isFloor ? '-5px' : '-2px', width: isFloor ? '3px' : '1px', background: isFloor ? '#EEBB3F' : 'rgba(255,255,255,0.35)', transform: 'translateX(-50%)' }}>
              <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px', fontSize: '10px', color: isFloor ? '#EEBB3F' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', fontWeight: isFloor ? 700 : 400 }}>{pct(p.att, 0)}{p.beyond ? '+' : ''}</span>
            </div>
          );
        })}
        {/* Marker */}
        <div style={{ position: 'absolute', left: `${fillPct}%`, top: '50%', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', border: '3px solid #00A4E4', transform: 'translate(-50%, -50%)', boxShadow: '0 0 0 4px rgba(0,164,228,0.25)' }} title={`You are at ${pct(att, 1)}`} />
      </div>
      {next ? (
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-primary)' }}>{fmt(Math.max(0, next.att * comp1.floor - comp1.servicesIncome))}</strong> more services income reaches the <strong style={{ color: 'var(--text-primary)' }}>{pct(next.att, 0)}</strong> band
          {' '}({fmt0(next.att * comp1.floor)} total) and pays <strong style={{ color: 'var(--green)' }}>{fmt0(next.pct * comp1.target)}</strong>.
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Above the last published point. Every further 10 points of attainment ({fmt0(0.1 * comp1.floor)} of income) adds <strong style={{ color: 'var(--green)' }}>{fmt0(0.1 * slope * comp1.target)}</strong>. No ceiling.
        </div>
      )}
    </div>
  );
}

function BandLadder({ bands, target, attainment, floor }) {
  const { points, slope } = ladderPoints(bands);
  const att = attainment || 0;
  const idx = activeIndex(points, att);
  const cells = [{ key: 'below', title: 'Below 75%', income: `under ${fmt0(points[0].att * floor)}`, payout: 0, pct: 0 }, ...points.map(p => ({
    key: String(p.att), title: `${pct(p.att, 0)}${p.beyond ? '+' : ''}`, sub: p.att === 1 ? 'the Floor' : p.beyond ? 'and up' : null,
    income: fmt0(p.att * floor), payout: p.pct * target, pct: p.pct,
  }))];
  const activeKey = idx < 0 ? 'below' : String(points[idx].att);
  return (
    <div>
      <div className="label" style={{ marginBottom: '10px' }}>Band ladder · attainment → payout</div>
      <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }} className="ops-ladder">
        {cells.map((c, i) => {
          const active = c.key === activeKey;
          const passed = !active && (activeKey !== 'below') && i < cells.findIndex(x => x.key === activeKey);
          return (
            <div key={c.key} style={{
              position: 'relative', padding: '14px 12px 12px', borderRadius: '10px', textAlign: 'center',
              border: `1px solid ${active ? 'transparent' : passed ? 'rgba(0,164,228,0.35)' : 'var(--border)'}`,
              background: active ? 'linear-gradient(145deg, #0079C2 0%, #00A4E4 100%)' : passed ? 'rgba(0,121,194,0.10)' : 'transparent',
              color: active ? '#fff' : 'var(--text-primary)',
              boxShadow: active ? '0 10px 24px rgba(0,121,194,0.35)' : 'none',
              transform: active ? 'translateY(-4px)' : 'none',
              transition: 'transform .15s, box-shadow .15s',
            }}>
              {active && (
                <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: '#EEBB3F', color: '#1A1A1A', fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.12em', padding: '3px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>YOU ARE HERE</div>
              )}
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '17px', fontWeight: 700 }}>{c.title}</div>
              <div style={{ fontSize: '10.5px', opacity: active ? 0.9 : 0.7, marginTop: '1px', minHeight: '13px' }}>{c.sub || ''}</div>
              <div style={{ fontSize: '11px', opacity: active ? 0.95 : 0.75, marginTop: '6px' }}>{c.income}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '14px', fontWeight: 700, marginTop: '6px', color: active ? '#fff' : c.payout > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{fmt0(c.payout)}</div>
              <div style={{ fontSize: '10.5px', opacity: active ? 0.9 : 0.6 }}>{pct(c.pct, 0)} of component</div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '10px' }}>Straight line between points. No ceiling: every further 10 points of attainment adds {fmt0(0.1 * slope * target)}.</div>
    </div>
  );
}

function Kv({ label, value, sub }) {
  return (
    <div>
      <div className="label" style={{ marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}
function Field({ label, children, wide }) {
  return <div style={wide ? { gridColumn: '1 / -1' } : undefined}><div className="label" style={{ marginBottom: '6px' }}>{label}</div>{children}</div>;
}
function Notice({ tone = 'muted', tight, children }) {
  const map = { amber: ['var(--amber-subtle)', 'var(--amber)'], red: ['var(--red-subtle)', 'var(--red)'], muted: ['var(--bg-elevated)', 'var(--border-bright)'] };
  const [bg, bd] = map[tone] || map.muted;
  return (
    <div style={{ background: bg, borderLeft: `3px solid ${bd}`, borderRadius: 'var(--radius-sm)', padding: tight ? '10px 14px' : '14px 18px', margin: tight ? '10px 0 0' : '0 0 18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
      {children}
    </div>
  );
}
